import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { m } from '@/lib/paraglide/messages'

/** Recipient info staff can adjust before printing a slip/label. Applies to
 *  that one print only — never written back to the order. */
export interface PrintInfoValues {
    customerName: string
    customerPhone: string
    shippingAddress: string
    shippingWard: string
    shippingProvince: string
    note: string
    /** Số tiền COD cho riêng lần in này. Giữ dạng string như các field khác —
     *  parse ở nơi dùng, tránh NaN rò vào state khi ô đang trống. */
    codAmount: string
    /** Số kiện cho lần in này (mỗi kiện một tờ nhãn). String như các field khác. */
    packageCount: string
}

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    /** Defaults read from the order; the form re-seeds from these on open. */
    initial: PrintInfoValues
    /** Hiện ô sửa tiền COD. Chỉ bật cho phiếu thực sự in dòng COD (nhãn thùng,
     *  phiếu giao hàng, và đơn có thu COD) — bật ở chỗ khác thì ô sẽ không có
     *  tác dụng gì. */
    showCod?: boolean
    /** Hiện ô số kiện. Chỉ nhãn thùng in theo kiện nên chỉ bật ở đó. */
    showPackageCount?: boolean
    onConfirm: (values: PrintInfoValues) => void
}

const TEXTAREA_CLS =
    'flex min-h-16 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50'

/**
 * Review-before-print dialog shared by the delivery slip, box label, and
 * packing slip buttons. Pre-filled from the order; staff can fix the
 * recipient block (name / phone / address / note) for this print only.
 */
export function PrintInfoDialog({
    open,
    onOpenChange,
    initial,
    showCod = false,
    showPackageCount = false,
    onConfirm,
}: Props) {
    const [values, setValues] = useState<PrintInfoValues>(initial)

    // Re-seed from the order on each OPEN TRANSITION so edits from a previous
    // print never leak into the next one (per-print semantics), while edits
    // are kept if `initial` gets a new identity mid-session (order refetch).
    const wasOpen = useRef(open)
    useEffect(() => {
        if (open && !wasOpen.current) setValues(initial)
        wasOpen.current = open
    }, [open, initial])

    const set =
        (field: keyof PrintInfoValues) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setValues((prev) => ({ ...prev, [field]: e.target.value }))

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{m.print_info_dialog_title()}</DialogTitle>
                    <DialogDescription>
                        {m.print_info_dialog_desc()}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label htmlFor="print-info-name">
                            {m.customer_name_label()}
                        </Label>
                        <Input
                            id="print-info-name"
                            value={values.customerName}
                            onChange={set('customerName')}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="print-info-phone">
                            {m.customer_phone_label()}
                        </Label>
                        <Input
                            id="print-info-phone"
                            value={values.customerPhone}
                            onChange={set('customerPhone')}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="print-info-address">
                            {m.customer_address_label()}
                        </Label>
                        <textarea
                            id="print-info-address"
                            className={TEXTAREA_CLS}
                            value={values.shippingAddress}
                            onChange={set('shippingAddress')}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="print-info-ward">
                                {m.customer_ward_label()}
                            </Label>
                            <Input
                                id="print-info-ward"
                                value={values.shippingWard}
                                onChange={set('shippingWard')}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label htmlFor="print-info-province">
                                {m.customer_province_label()}
                            </Label>
                            <Input
                                id="print-info-province"
                                value={values.shippingProvince}
                                onChange={set('shippingProvince')}
                            />
                        </div>
                    </div>
                    {showCod && (
                        <div className="space-y-1">
                            <Label htmlFor="print-info-cod">
                                {m.box_label_cod_collect()}
                            </Label>
                            <Input
                                id="print-info-cod"
                                type="number"
                                min={0}
                                step="any"
                                value={values.codAmount}
                                onChange={set('codAmount')}
                            />
                            <p className="text-xs text-muted-foreground">
                                {m.print_info_cod_hint()}
                            </p>
                        </div>
                    )}
                    {showPackageCount && (
                        <div className="space-y-1">
                            <Label htmlFor="print-info-package-count">
                                {m.box_label_package_count()}
                            </Label>
                            <Input
                                id="print-info-package-count"
                                type="number"
                                min={1}
                                step={1}
                                value={values.packageCount}
                                onChange={set('packageCount')}
                            />
                            <p className="text-xs text-muted-foreground">
                                {m.print_info_package_count_hint()}
                            </p>
                        </div>
                    )}
                    <div className="space-y-1">
                        <Label htmlFor="print-info-note">
                            {m.box_label_note()}
                        </Label>
                        <textarea
                            id="print-info-note"
                            className={TEXTAREA_CLS}
                            value={values.note}
                            onChange={set('note')}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        {m.common_cancel()}
                    </Button>
                    <Button type="button" onClick={() => onConfirm(values)}>
                        {m.print_info_dialog_print_btn()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
